/**
 * A tela de senha nova, alcancada pelo link do e-mail (historia 1.4).
 *
 * **O caminho desta rota tem dois donos.** O backend escreve o link do e-mail com
 * `caminho_publico_da_redefinicao` (`crm-backend/compartilhado/configuracao.py`, padrao
 * `/redefinir-senha`); esta rota e o outro lado dele. Mudar um sem o outro quebra em silencio
 * **todo e-mail ja enviado**, e nenhum teste dos dois repositorios ve a divergencia — e o
 * DW-22. Por isso a constante abaixo e exportada e comentada: quem mexer aqui tem de mexer la.
 *
 * Sem `?token=` na URL a tela **nao chama a API**: nao ha o que enviar, e um `POST` com token
 * vazio so trocaria uma explicacao clara por uma recusa generica do servidor. O mesmo vale para
 * um token que e so espaco em branco — um link truncado no e-mail —, por isso ele e aparado
 * antes de valer como token.
 *
 * A senha nova tambem e conferida **antes** da rede: o minimo de 8 caracteres e a igualdade das
 * duas digitadas sao coisas que o cliente ja sabe, e a mesma regra vale na tela de entrada.
 *
 * Sucesso nao autentica ninguem: a redefinicao revoga **todas** as sessoes do usuario, entao o
 * cache de consultas e **limpo** e a tela seguinte e a de entrada. Sem essa limpeza, quem trocou
 * a senha de dentro do sistema voltaria ao shell com uma sessao que o servidor ja derrubou.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { redefinirSenha } from '@/modulos/identidade/api'
import { rotaPublica } from './publico'
import './entrar.css'

/**
 * O caminho da tela de nova senha.
 *
 * Espelha `caminho_publico_da_redefinicao` do `crm-backend`, que e a base do link do e-mail
 * (DW-22). Os dois mudam juntos ou nao mudam.
 */
export const CAMINHO_DA_REDEFINICAO = '/redefinir-senha'

const FALTA_SENHA = 'Informe a senha nova.'
const SENHAS_DIFERENTES = 'As duas senhas precisam ser iguais.'

/**
 * O minimo que o servico aceita.
 *
 * Espelha `TAMANHO_MINIMO_DA_SENHA` de `servicos/identidade/dominio/usuario.py`. Conferir aqui
 * segue a mesma regra da tela de entrada — nao gastar ida a rede com o que o cliente ja sabe —,
 * e o servidor continua sendo quem decide: se o minimo mudar la e nao aqui, a recusa dele chega
 * verbatim e a tela a mostra.
 */
const TAMANHO_MINIMO_DA_SENHA = 8
const SENHA_CURTA = `A nova senha precisa ter ao menos ${String(TAMANHO_MINIMO_DA_SENHA)} caracteres.`

interface BuscaDaRedefinicao {
  token?: string
}

function RedefinirSenha() {
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const { token } = rotaRedefinirSenha.useSearch()

  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const redefinir = useMutation({
    mutationFn: (nova: string) => redefinirSenha({ token: token ?? '', senha: nova }),
  })

  if (token === undefined || token === '') {
    return (
      <>
        <h1 className="entrada__titulo">Link incompleto</h1>
        <p className="entrada__texto">
          Este endereço só funciona com o link que chega por e-mail — ele carrega o código que
          autoriza a troca. Peça um link novo e abra-o direto do e-mail.
        </p>
        <Link to="/esqueci-minha-senha" className="entrada__link">
          Pedir um link novo
        </Link>
      </>
    )
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()

    if (senha === '') {
      setErro(FALTA_SENHA)
      return
    }
    if (senha.length < TAMANHO_MINIMO_DA_SENHA) {
      setErro(SENHA_CURTA)
      return
    }
    if (senha !== confirmacao) {
      setErro(SENHAS_DIFERENTES)
      return
    }

    setErro(null)
    redefinir.mutate(senha, {
      onSuccess: () => {
        // A redefinicao revoga **todas** as sessoes do usuario. Sem limpar o cache, quem
        // trocou a senha estando logado continuaria com `['sessao']` gravado, e a guarda o
        // deixaria voltar para `/hoje` com cookies que o servidor ja invalidou — uma tela
        // inteira montada sobre uma sessao que nao existe mais.
        clienteDeConsultas.clear()
        void navegar({ to: '/entrar' })
      },
      onError: (falha: unknown) => {
        setErro(falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO)
      },
    })
  }

  return (
    <>
      <h1 className="entrada__titulo">Nova senha</h1>
      <p className="entrada__texto">
        Escolha a senha nova. Ao salvar, todas as sessões abertas com a senha antiga são
        encerradas — inclusive as de outros aparelhos.
      </p>

      <form className="entrada__formulario" onSubmit={enviar} noValidate>
        <div className="entrada__campo">
          <label className="entrada__rotulo" htmlFor="campo-senha">
            Senha nova
          </label>
          <input
            id="campo-senha"
            className="entrada__entrada"
            type="password"
            name="senha"
            autoComplete="new-password"
            autoFocus
            value={senha}
            onChange={(evento) => {
              setSenha(evento.target.value)
            }}
          />
        </div>

        <div className="entrada__campo">
          <label className="entrada__rotulo" htmlFor="campo-confirmacao">
            Repita a senha
          </label>
          <input
            id="campo-confirmacao"
            className="entrada__entrada"
            type="password"
            name="confirmacao"
            autoComplete="new-password"
            value={confirmacao}
            onChange={(evento) => {
              setConfirmacao(evento.target.value)
            }}
          />
        </div>

        {erro === null ? null : (
          <p className="entrada__erro" role="alert">
            {erro}
          </p>
        )}

        <button className="entrada__botao" type="submit" disabled={redefinir.isPending}>
          {redefinir.isPending ? 'Salvando…' : 'Salvar senha'}
        </button>
      </form>

      <Link to="/entrar" className="entrada__link">
        Voltar para entrar
      </Link>
    </>
  )
}

export const rotaRedefinirSenha = createRoute({
  getParentRoute: () => rotaPublica,
  path: CAMINHO_DA_REDEFINICAO,
  component: RedefinirSenha,
  /**
   * O token vem cru da query, e so isso: validar formato aqui seria adivinhar o que o backend
   * emitiu. Link invalido, expirado ou ja usado sobem como a mesma recusa `400` — e nem deve
   * haver como distinguir os tres.
   */
  validateSearch: (busca: Record<string, unknown>): BuscaDaRedefinicao => {
    // `trim` porque `?token=%20` e um link truncado, e nao um token: sem isto ele viraria um
    // `POST` com espaco em branco e uma recusa generica do servidor, em vez da explicacao de
    // link incompleto que esta tela sabe dar.
    const token = typeof busca.token === 'string' ? busca.token.trim() : ''
    return { token: token === '' ? undefined : token }
  },
})
